<template>
  <b-card v-if="!loading">
    <div slot="header"><i class="fa fa-trophy"></i> Server Leaderboard</div>

    <!-- Search Section -->
    <div class="mb-3">
      <h5 class="tinytitle">Search Users</h5>
      <p>Find specific users in the leaderboard by username or user id</p>

      <b-form @submit.prevent="onSearchSubmit">
        <b-input-group style="width: 50%">
          <b-form-input
            v-model="searchUsername"
            placeholder="Enter username or user id"
            :disabled="loading"
          />
          <b-input-group-append>
            <b-button
              type="submit"
              variant="primary"
              class="ml-2"
              style="height: 33px"
              :disabled="
                loading ||
                !searchUsername.trim() ||
                searchUsername.trim().length > 30
              "
            >
              Search
            </b-button>
            <b-button
              v-if="isSearchMode"
              style="height: 33px"
              class="ml-1"
              variant="outline-secondary"
              @click="clearSearch"
              :disabled="loading"
            >
              Clear
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form>

      <!-- Search Results Info -->
      <div v-if="isSearchMode" class="mt-2">
        <p v-if="!rate_limit_text">
          Showing search results for "{{ searchUsername_temp }}" ({{
            users.length
          }}

          {{ users.length === 1 ? "user" : "users" }} found)
          <span v-if="users.length === 0 && !rate_limit_text">
            - User does not exist in leaderboard or has left the server
          </span>
        </p>
        <p v-if="rate_limit_text">
          {{ rate_limit_text }}
        </p>
      </div>
    </div>

    <hr />

    <!-- Leaderboard Table -->
    <v-client-table
      :columns="columns"
      :data="users"
      :options="options"
      :theme="theme"
      id="leaderboardTable"
      style="width: 100%; margin: none"
    >
    </v-client-table>

    <!-- Manual Pagination (only show when not in search mode) -->
    <div v-if="!isSearchMode" class="text-center mt-3">
      <b-button
        variant="outline-primary"
        class="mr-2"
        :disabled="page === 1 || loading"
        @click="changePage(page - 1)"
      >
        « Previous
      </b-button>

      <span class="mx-2">Page {{ page }}</span>

      <b-button
        variant="outline-primary"
        class="ml-2"
        :disabled="!hasNextPage || loading"
        @click="changePage(page + 1)"
      >
        Next »
      </b-button>
    </div>
  </b-card>

  <!-- Loading State -->
  <b-card v-else>
    <div slot="header"><i class="fa fa-trophy"></i> Server Leaderboard</div>
    <div class="text-center">
      <div class="sk-circle-fade float-left">
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
        <div class="sk-grid-cube"></div>
      </div>
      <h2 class="float-left ml-4">Loading leaderboard...</h2>
    </div>
  </b-card>
</template>

<script>
import Vue from "vue";
import { ClientTable } from "vue-tables-2";

Vue.use(ClientTable);

export default {
  name: "Leaderboard",
  props: {
    guildId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      users: [],
      page: 1,
      limit: 10,
      hasNextPage: false,
      searchUsername: "",
      searchUsername_temp: "",
      loading: false,
      isSearchMode: false,
      rate_limit_text: "",
      columns: ["rank", "username", "xp", "level"],
      options: {
        filterByColumn: false,
        filterable: false,
        sortable: [],
        pagination: {
          chunk: 10,
          edge: false,
          nav: "scroll",
        },
        texts: {
          filter: "Filter leaderboard:",
        },
        headings: {
          rank: "Rank",
          username: "Username",
          xp: "XP",
          level: "Level",
        },
        sortIcon: {
          base: "fa",
          up: "fa-sort-asc",
          down: "fa-sort-desc",
          is: "fa-sort",
        },
        childRow: false,
        highlightMatches: true,
      },
      theme: "bootstrap4",
    };
  },
  created() {
    this.fetchLeaderboard();
  },
  methods: {
    async fetchLeaderboard(page = 1) {
      this.loading = true;

      try {
        // Fetch current page
        const currentPageRes = await this.axios.get(
          `/api/v1/servers/${this.guildId}/levels/leaderboard`,
          { params: { page, limit: this.limit } }
        );

        // Fetch next page to check if it exists
        const nextPageRes = await this.axios.get(
          `/api/v1/servers/${this.guildId}/levels/leaderboard`,
          { params: { page: page + 1, limit: this.limit } }
        );

        // Update current page data - directly use the users array from API
        this.users = currentPageRes.data.users || [];
        this.users.forEach((user, index) => {
          if (!user.username) {
            user.username = "User: " + user.user_id; // Fallback to user_id if username is missing
          }
        });
        this.page = page;

        // Check if next page has users to determine if "Next" button should be enabled
        this.hasNextPage = (nextPageRes.data.users || []).length > 0;
      } catch (error) {
        console.error("Error fetching leaderboard:", error);

        // Reset data on error
        this.users = [];
        this.hasNextPage = false;
      } finally {
        this.loading = false;
      }
    },

    changePage(newPage) {
      if (newPage >= 1) {
        this.fetchLeaderboard(newPage);
      }
    },

    async searchUsers(query) {
      this.loading = true;
      this.rate_limit_text = "";
      this.searchUsername_temp = this.searchUsername;

      try {
        const res = await this.axios.get(
          `/api/v1/servers/${this.guildId}/levels/leaderboard/search`,
          { params: { query } }
        );

        this.users = res.data || [];
        this.isSearchMode = true;
      } catch (error) {
        console.error("Error searching users:", error);
        if (error.response.status == 429) {
          this.rate_limit_text =
            "Cannot process the search request at this time. Please try again in a moment.";
        }
        console.log(this.rate_limit_text);
        this.users = [];
      } finally {
        this.loading = false;
      }
    },

    onSearchSubmit() {
      if (!this.searchUsername.trim()) {
        return;
      }
      this.searchUsers(this.searchUsername.trim());
    },

    clearSearch() {
      this.searchUsername = "";
      this.rate_limit_text = "";
      this.isSearchMode = false;
      this.searchUsername_temp = "";
      this.page = 1;
      this.fetchLeaderboard(1);
    },
  },
};
</script>

<style lang="scss">
#leaderboardTable {
  width: 95%;
  margin: 0 auto;

  .VuePagination {
    text-align: center;
    justify-content: center;
  }

  .vue-title {
    text-align: center;
    margin-bottom: 10px;
  }

  .VueTables__search-field {
    display: flex;
  }
  .VueTables__search-field input {
    margin-left: 0.25rem;
  }

  .VueTables__limit-field {
    display: flex;
  }

  .VueTables__limit-field select {
    margin-left: 0.25rem !important;
  }

  .VueTables__table th {
    text-align: center;
  }

  .VueTables__child-row-toggler {
    width: 16px;
    height: 16px;
    line-height: 16px;
    display: block;
    margin: auto;
    text-align: center;
  }

  .VueTables__child-row-toggler--closed::before {
    content: "+";
  }

  .VueTables__child-row-toggler--open::before {
    content: "-";
  }
}
</style>
